import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, Clock, Users, MapPin, ChefHat, Phone, Mail, Star, Filter, DollarSign, Utensils, Loader, AlertCircle, Building2, Heart, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingIndicator from '../components/LoadingIndicator';
import FilterSection from '../components/FilterSection';
import { cuisineOptions, priceRangeOptions, dietaryOptions } from '../data/filterOptions';
import BackButton from '../components/BackButton';
import { createReservation, shareReservationViaChat } from '../lib/reservations';
import UserSearchModal from '../components/UserSearchModal';

const Reservations = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [animateCards, setAnimateCards] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState<any[]>([]);
  
  const [reservationDetails, setReservationDetails] = useState({
    date: '',
    time: '',
    partySize: 2,
    specialRequests: ''
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    // Trigger card animations after initial load
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [restaurants]);

  useEffect(() => {
    // Filter restaurants based on search term and filters
    let filtered = restaurants;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(restaurant => 
        restaurant.name.toLowerCase().includes(term) || 
        (restaurant.cuisine_type && restaurant.cuisine_type.toLowerCase().includes(term))
      );
    }
    
    if (selectedFilters.length > 0) {
      filtered = filtered.filter(restaurant => {
        // Check if any selected filter matches the restaurant
        return selectedFilters.some(filter => {
          // Special case for price range
          if (filter === '?' && restaurant.price_range) {
            return true;
          }
          
          // Check price range
          if (filter === restaurant.price_range) {
            return true;
          }
          
          // Check cuisine type
          if (restaurant.cuisine_type && restaurant.cuisine_type.toLowerCase() === filter.toLowerCase()) {
            return true;
          }
          
          // Check dietary options
          if (restaurant.dietary_options && restaurant.dietary_options.includes(filter)) {
            return true;
          }
          
          return false;
        });
      });
    }
    
    setFilteredRestaurants(filtered);
  }, [restaurants, searchTerm, selectedFilters]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('status', 'approved')
        .order('name');

      if (fetchError) throw fetchError;
      setRestaurants(data || []);
      setFilteredRestaurants(data || []);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleReservationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRestaurant) return;

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Create the reservation
      const reservationData = {
        restaurant_id: selectedRestaurant.id,
        date: reservationDetails.date,
        time: reservationDetails.time,
        party_size: reservationDetails.partySize,
        special_requests: reservationDetails.specialRequests
      };
      
      await createReservation(reservationData);

      // Show success message
      setSuccessMessage('Reservation created successfully!');

      // Reset form and show success message
      setReservationDetails({
        date: '',
        time: '',
        partySize: 2,
        specialRequests: ''
      });

      // Navigate to My Reservations page after a short delay
      setTimeout(() => {
        navigate('/my-reservations');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit reservation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShareReservation = async (userId: string, userName: string) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      const reservationData = {
        restaurant_id: selectedRestaurant.id,
        date: reservationDetails.date,
        time: reservationDetails.time,
        party_size: reservationDetails.partySize,
        special_requests: reservationDetails.specialRequests
      };
      
      const result = await shareReservationViaChat(
        userId, 
        selectedRestaurant.name,
        reservationData
      );
      
      setSuccessMessage(`Reservation shared with ${userName}!`);
      
      // Navigate to chat with this user
      setTimeout(() => {
        navigate(`/chat/${result.chatRoomId}`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share reservation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg flex items-center">
          <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                {t('reservations.title')}
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                {t('reservations.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('reservations.searchPlaceholder')}
              className="pl-10 w-full p-3 border-2 border-gray-200 rounded-xl transition-all duration-300 focus:border-orange-500 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Filter className="h-5 w-5" />
            <span>{showFilters ? t('search.hideFilters') : t('search.showFilters')}</span>
          </button>
        </div>

        {showFilters && (
          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t animate-slideDown">
            <FilterSection
              title={t('search.cuisine')}
              icon={ChefHat}
              options={cuisineOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('search.priceRange')}
              icon={DollarSign}
              options={priceRangeOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('common.dietaryOptions')}
              icon={Utensils}
              options={dietaryOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Restaurant List */}
        <div className="lg:col-span-2">
          <div className="grid md:grid-cols-2 gap-6">
            {filteredRestaurants.map((restaurant, index) => (
              <div
                key={restaurant.id}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 cursor-pointer ${
                  selectedRestaurant?.id === restaurant.id ? 'ring-2 ring-orange-500' : ''
                } ${
                  animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => setSelectedRestaurant(restaurant)}
              >
                {restaurant.pictures?.exterior?.[0] ? (
                  <div className="relative h-48 overflow-hidden group">
                    <img
                      src={restaurant.pictures.exterior[0]}
                      alt={restaurant.name}
                      className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                ) : (
                  <div className="h-48 bg-gray-200 flex items-center justify-center">
                    <Building2 className="h-12 w-12 text-gray-400" />
                  </div>
                )}

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {restaurant.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <ChefHat className="h-4 w-4" />
                        <span>{restaurant.cuisine_type}</span>
                        <span>•</span>
                        <span>{restaurant.price_range}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-yellow-100 px-2 py-1 rounded-full">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-yellow-700 font-medium">
                        {restaurant.rating || '4.5'} ({restaurant.reviews_count || '0'})
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{restaurant.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{restaurant.opening_hours}</span>
                    </div>
                    {restaurant.features?.outdoor_seating && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <Heart className="h-4 w-4" />
                        <span>{t('reservations.outdoorSeating')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reservation Form */}
        {selectedRestaurant && (
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="text-xl font-semibold mb-6">{t('reservations.makeReservation')}</h3>
              
              {successMessage && (
                <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  <span>{successMessage}</span>
                </div>
              )}
              
              <form onSubmit={handleReservationSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.date')}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      className="pl-10 w-full p-2 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={reservationDetails.date}
                      onChange={(e) => setReservationDetails({
                        ...reservationDetails,
                        date: e.target.value
                      })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.time')}
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <select
                      className="pl-10 w-full p-2 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={reservationDetails.time}
                      onChange={(e) => setReservationDetails({
                        ...reservationDetails,
                        time: e.target.value
                      })}
                      required
                    >
                      <option value="">{t('reservations.selectTime')}</option>
                      {['17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'].map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reservations.partySize')}
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <select
                      className="pl-10 w-full p-2 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={reservationDetails.partySize}
                      onChange={(e) => setReservationDetails({
                        ...reservationDetails,
                        partySize: parseInt(e.target.value)
                      })}
                      required
                    >
                      {[...Array(10)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {i === 0 ? t('reservations.person') : t('reservations.people')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reservations.specialRequests')}
                  </label>
                  <textarea
                    className="w-full p-2 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={4}
                    placeholder={t('reservations.specialRequestsPlaceholder')}
                    value={reservationDetails.specialRequests}
                    onChange={(e) => setReservationDetails({
                      ...reservationDetails,
                      specialRequests: e.target.value
                    })}
                  ></textarea>
                </div>

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 text-white px-4 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-75"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Calendar className="h-5 w-5" />
                        <span>{t('reservations.completeReservation')}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {filteredRestaurants.length === 0 && (
          <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-12 text-center">
            <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-6" />
            <h3 className="text-xl font-medium text-gray-900 mb-4">{t('reservations.noResults')}</h3>
            <p className="text-gray-600">
              {t('reservations.noResultsMessage')}
            </p>
          </div>
        )}
      </div>

      {/* User Search Modal for sharing reservations */}
      <UserSearchModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onSelectUser={handleShareReservation}
      />
    </div>
  );
};

export default Reservations;